import {
  updateOrganization,
  OnboardingProgress,
  OnboardingUpdateRequest
} from "./OrganizationService";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { safeLocalStorageKeys, safeRemoveItemSync } from "@/utils/safeStorage";

export interface OnboardingAction {
  id: string;
  step: "step1" | "step2";
  progressKey: keyof OnboardingProgress;
}

// Mapping of action IDs to progress keys
export const ACTION_TO_PROGRESS_MAP: Record<string, keyof OnboardingProgress> =
  {
    // Step 1 - Customer Sync Actions
    customers: "customer",
    "customer-overview": "customersReportOverview",
    "behaviour-engagement": "behaviorAndEngagementReport",
    "purchase-patterns": "purchasePatternReport",
    reports: "customersReportOverview",
    sms: "smsCampaigns",

    // Step 2 - Order Sync Actions
    products: "productCatalog",
    "product-insights": "productInsightsReport",
    transactions: "transactions",
    "transaction-reports": "transactionReportOverview",
    "sales-overview": "salesReportOverview",
    "trends-patterns": "trendsAndPatternsReport",
    "automated-campaigns": "automatedCampaigns"
  };

export class OnboardingProgressService {
  private static instance: OnboardingProgressService;
  private sessionStartTime: number = Date.now();
  private sessionTimeout: number = 30 * 60 * 1000; // 30 minutes
  private pendingUpdates: Set<string> = new Set();
  private updateQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue = false;

  private constructor() {
    // Set up session expiry handling
    this.setupSessionExpiryHandling();
    // Set up logout handling
    this.setupLogoutHandling();
  }

  public static getInstance(): OnboardingProgressService {
    if (!OnboardingProgressService.instance) {
      OnboardingProgressService.instance = new OnboardingProgressService();
    }
    return OnboardingProgressService.instance;
  }

  /**
   * Mark an action as completed and update progress
   */
  public async markActionCompleted(actionId: string): Promise<void> {
    const progressKey = ACTION_TO_PROGRESS_MAP[actionId];
    if (!progressKey) {
      console.warn(`No progress key found for action: ${actionId}`);
      return;
    }

    this.pendingUpdates.add(progressKey);
    await this.queueProgressUpdate(progressKey, "DONE");

    // Trigger a refresh of onboarding progress to sync with backend
    const { refreshOnboardingProgress } = useOrganizationStore.getState();
    refreshOnboardingProgress();
  }

  /**
   * Mark all actions in a step as completed
   */
  public async markStepCompleted(step: "step1" | "step2"): Promise<void> {
    const { completedStep1Actions, completedStep2Actions } =
      useOrganizationStore.getState();
    const completedActions =
      step === "step1" ? completedStep1Actions : completedStep2Actions;

    const updates: Array<{ key: keyof OnboardingProgress; status: "DONE" }> =
      [];

    completedActions.forEach((actionId) => {
      const progressKey = ACTION_TO_PROGRESS_MAP[actionId];
      if (progressKey) {
        updates.push({ key: progressKey, status: "DONE" });
        this.pendingUpdates.add(progressKey);
      }
    });

    await this.queueBulkProgressUpdate(updates);
  }

  /**
   * Check if all actions are completed and update showOnboardingFlow
   */
  public async checkAndUpdateOnboardingCompletion(): Promise<void> {
    const { completedStep1Actions, completedStep2Actions } =
      useOrganizationStore.getState();
    const { syncDataResponse } = useOrganizationStore.getState();

    const customerSyncCompleted = syncDataResponse.customer === 100;
    const orderSyncCompleted = syncDataResponse.orders === 100;

    // Get all possible actions for both steps
    const step1Actions = this.getStep1Actions();
    const step2Actions = this.getStep2Actions();

    const isStep1Complete = completedStep1Actions.size === step1Actions.length;
    const isStep2Complete = completedStep2Actions.size === step2Actions.length;

    // If both syncs are complete and all actions are done, hide onboarding
    if (
      customerSyncCompleted &&
      orderSyncCompleted &&
      isStep1Complete &&
      isStep2Complete
    ) {
      await this.queueProgressUpdate(null, null, false); // Hide onboarding flow

      // Trigger a refresh of onboarding progress to sync with backend
      const { refreshOnboardingProgress } = useOrganizationStore.getState();
      refreshOnboardingProgress();
    }
  }

  /**
   * Save progress before session expires
   */
  public async saveProgressOnSessionExpiry(): Promise<void> {
    const {
      completedStep1Actions,
      completedStep2Actions,
      currentOrganizationId
    } = useOrganizationStore.getState();

    // Only save if we have an organization ID
    if (!currentOrganizationId) {
      // No organization ID available, so skipping progress save
      return;
    }

    const updates: Array<{ key: keyof OnboardingProgress; status: "DONE" }> =
      [];
    // Save all completed actions (not just pending ones)
    [...completedStep1Actions, ...completedStep2Actions].forEach((actionId) => {
      const progressKey = ACTION_TO_PROGRESS_MAP[actionId];
      if (progressKey) {
        updates.push({ key: progressKey, status: "DONE" });
      } else {
        console.warn(`No progress key found for action: ${actionId}`);
      }
    });

    if (updates.length > 0) {
      await this.queueBulkProgressUpdate(updates);
    } else {
      console.log("No completed actions to save");
    }
  }

  /**
   * Save progress before logout
   */
  public async saveProgressOnLogout(): Promise<void> {
    await this.saveProgressOnSessionExpiry();
  }

  /**
   * Queue a single progress update
   */
  private async queueProgressUpdate(
    progressKey: keyof OnboardingProgress | null,
    status: "IN_PROGRESS" | "DONE" | null,
    showOnboardingFlow?: boolean
  ): Promise<void> {
    const updateFn = async () => {
      try {
        const payload: OnboardingUpdateRequest = {};

        if (progressKey && status) {
          // Get current onboarding progress from the store to merge with new updates
          const { onboardingProgress } = useOrganizationStore.getState();

          if (onboardingProgress) {
            // Merge current progress with the new update
            payload.onboardingProgress = {
              ...onboardingProgress,
              [progressKey]: status
            };
          } else {
            // Fallback: just update the specific key
            payload.onboardingProgress = { [progressKey]: status };
          }
        }

        if (showOnboardingFlow !== undefined) {
          payload.showOnboardingFlow = showOnboardingFlow;
        }

        await updateOrganization(payload as any);

        if (progressKey) {
          this.pendingUpdates.delete(progressKey);
        }
      } catch (error) {
        console.error("Failed to update onboarding progress:", error);
      }
    };

    this.updateQueue.push(updateFn);
    await this.processQueue();
  }

  /**
   * Queue bulk progress updates
   */
  private async queueBulkProgressUpdate(
    updates: Array<{ key: keyof OnboardingProgress; status: "DONE" }>
  ): Promise<void> {
    if (updates.length === 0) return;

    const updateFn = async () => {
      try {
        // Get current onboarding progress from the store to merge with new updates
        const { onboardingProgress: currentProgress } =
          useOrganizationStore.getState();

        const onboardingProgress: Partial<OnboardingProgress> = currentProgress
          ? { ...currentProgress }
          : {};

        updates.forEach(({ key, status }) => {
          onboardingProgress[key] = status;
        });

        await updateOrganization({ onboardingProgress } as any);

        updates.forEach(({ key }) => {
          this.pendingUpdates.delete(key);
        });
      } catch (error) {
        console.error("Failed to update bulk onboarding progress:", error);
      }
    };

    this.updateQueue.push(updateFn);
    await this.processQueue();
  }

  /**
   * Process the update queue
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.updateQueue.length === 0) return;

    this.isProcessingQueue = true;

    while (this.updateQueue.length > 0) {
      const updateFn = this.updateQueue.shift();
      if (updateFn) {
        await updateFn();
        // Add small delay between API calls to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Set up session expiry handling
   */
  private setupSessionExpiryHandling(): void {
    // Check session expiry every minute
    setInterval(() => {
      const now = Date.now();
      if (now - this.sessionStartTime > this.sessionTimeout) {
        this.saveProgressOnSessionExpiry();
        this.sessionStartTime = now; // Reset session timer
      }
    }, 60000); // Check every minute

    // Also check on page visibility change
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.saveProgressOnSessionExpiry();
      } else {
        this.sessionStartTime = Date.now(); // Reset session timer when page becomes visible
      }
    });
  }

  /**
   * Set up logout handling
   */
  private setupLogoutHandling(): void {
    // Handle beforeunload event
    window.addEventListener("beforeunload", () => {
      this.saveProgressOnLogout();
    });

    // Handle pagehide event (for mobile browsers)
    window.addEventListener("pagehide", () => {
      this.saveProgressOnLogout();
    });
  }

  /**
   * Get step 1 actions based on app type
   */
  private getStep1Actions(): string[] {
    // This should match the logic in OnboardingSyncProgress component
    // For now, returning default CRM actions
    return ["customers", "reports", "sms"];
  }

  /**
   * Get step 2 actions based on app type
   */
  private getStep2Actions(): string[] {
    // This should match the logic in OnboardingSyncProgress component
    // For now, returning default CRM actions
    return [
      "products",
      "transactions",
      "transaction-reports",
      "automated-campaigns"
    ];
  }

  /**
   * Reset session timer (call when user is active)
   */
  public resetSessionTimer(): void {
    this.sessionStartTime = Date.now();
  }

  /**
   * Clear old localStorage entries for onboarding progress
   * This helps prevent cross-organization contamination
   */
  public static clearOldOnboardingEntries(): void {
    try {
      // Get all localStorage keys
      const keys = safeLocalStorageKeys();

      // Find and remove old onboarding entries (without organization ID)
      const oldKeys = keys.filter(
        (key) =>
          key === "completedStep1Actions" || key === "completedStep2Actions"
      );

      oldKeys.forEach((key) => {
        safeRemoveItemSync(key);
      });
    } catch (error) {
      console.warn("Failed to clear old onboarding entries:", error);
    }
  }
}

// Export singleton instance
export const onboardingProgressService =
  OnboardingProgressService.getInstance();
